import { useRef } from 'react';

import {
  PageContainer,
  ContainerSizes,
  Button,
  ButtonSizes,
  Typography,
} from '@repo/ui';

import { Header, ActionsCard } from 'components';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import useConditions from 'services/conditions/useConditions';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import { ConditionsList } from './ConditionsList';
import { ActionPlanItem } from 'types/ActionPlan';

import styles from './EmergingResearch.module.scss';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';

export const EmergingResearch = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();

  const { biomarkers, associations } = useConditions({
    kitId: currentKitId,
  });

  const { data: actionPlanItems } = useActionPlanData({
    kitId: currentKitId,
  });

  const actionsRef = useRef<HTMLDivElement>(null);

  // Filter for actions related to Biomarkers (Conditions)
  const actionPlanItemsFiltered = !actionPlanItems?.length
    ? []
    : actionPlanItems
        .filter(
          (item: ActionPlanItem) =>
            !!item.desired_outcomes?.length &&
            item.desired_outcomes.some(
              outcome => outcome.section === 'Biomarkers',
            ),
        )
        .map((item: ActionPlanItem) => ({
          ...item,
          desired_outcomes: item.desired_outcomes?.filter(
            outcome => outcome.section === 'Biomarkers',
          ),
        }));

  return (
    <>
      <Breadcrumb className={styles.breadcrumb} />
      <Header title='Emerging Research' />
      <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
        <TwoColumnGrid>
          <TwoColumnGrid.Wide>
            <div className={styles.contentWrapper}>
              <section id='conditions' className='mb-4'>
                <div className='flex align-items-center justify-between w-100 mb-2'>
                  <Typography variant='heading-l'>
                    Condition Biomarkers
                  </Typography>
                </div>
                <Typography variant='body-s'>
                  See how your microbiome signature associates with risk of
                  certain conditions in later life
                </Typography>
                <div className='pt-2'>
                  <ConditionsList type='Biomarkers' conditions={biomarkers} />
                </div>
              </section>

              <section id='conditions' className={styles.conditions}>
                <div className='flex align-items-center justify-between w-100 mb-2'>
                  <Typography variant='heading-l'>
                    Condition Associations
                  </Typography>
                </div>
                <Typography variant='body-s'>
                  See other associations with conditions, based on emerging
                  research
                </Typography>
                <div className='pt-2'>
                  <ConditionsList
                    type='Associations'
                    conditions={associations}
                  />
                </div>
              </section>
            </div>
          </TwoColumnGrid.Wide>
          <TwoColumnGrid.Slim isSticky>
            <div ref={actionsRef}>
              <ActionsCard
                title='Action Items'
                actions={actionPlanItemsFiltered}
              />
            </div>
          </TwoColumnGrid.Slim>
        </TwoColumnGrid>
      </PageContainer>
    </>
  );
};
